import {
  Checkbox,
  DataTable,
  InlineNotification,
  Search,
  Tag,
  SkeletonText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Toggle,
} from '@carbon/react';
import {
  allows,
  asProblem,
  crudApi,
  EmptyState,
  hasPermission,
  problemMessage,
  useAdmin,
  type CrudRecord,
  type RecordValues,
} from '@pos/admin-ui';
import type { HoComponents } from '@pos/api-client';
import { localized, useTranslation, type I18nKey } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';
import './permissions.scss';

type Permission = HoComponents['schemas']['PermissionDto'];
type Granted = HoComponents['schemas']['RolePermissionDto'];

/** Every page of a list: there are a few hundred permissions, more than one page. */
async function listAll<T>(path: string): Promise<T[]> {
  const api = crudApi<CrudRecord, never>(hoApi, path);
  const items: T[] = [];
  for (let page = 1; ; page++) {
    const result = await api.list({ page, pageSize: 100 });
    items.push(...(result.items as unknown as T[]));
    if (items.length >= result.total || result.items.length === 0) return items;
  }
}

/** One line of the grid: a feature with its read and manage permissions, or an action allowed on its own (void a line, export). */
interface FeatureRow {
  key: string;
  read?: Permission;
  manage?: Permission;
  single?: Permission;
}

/**
 * The features of an area in catalogue order: `x.read` and `x.manage` share a line; any other code is a line of its own. A line
 * whose code encloses others (`ho.catalog` over `ho.catalog.items`) is the whole area: it comes first.
 */
function featureRows(permissions: Permission[]): FeatureRow[] {
  const rows = new Map<string, FeatureRow>();
  for (const p of permissions) {
    const kind = p.code.endsWith('.read') ? 'read' : p.code.endsWith('.manage') ? 'manage' : 'single';
    const key = kind === 'single' ? p.code : p.code.slice(0, p.code.lastIndexOf('.'));
    const row = rows.get(key) ?? { key };
    row[kind] = p;
    rows.set(key, row);
  }
  const list = [...rows.values()];
  return [...list.filter((r) => isWholeArea(r, list)), ...list.filter((r) => !isWholeArea(r, list))];
}

const isWholeArea = (row: FeatureRow, rows: FeatureRow[]) =>
  !row.single && rows.some((other) => other.key.startsWith(`${row.key}.`));

/**
 * A role's permissions as a grid (FR-AUTH-12, ADR 0116): every feature there is, grouped by area, with *Read* and *Manage*, or
 * *Allowed* for an action on its own. What another permission already gives, as the server decides (manage includes read, the
 * whole area its screens), shows ticked and locked. A switch per area grants or takes all of it. Each change is saved at once.
 */
export function RolePermissionsTab({ role }: { role: RecordValues }) {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const canEdit = hasPermission(session, 'ho.org.roles.manage');
  const roleId = String(role.id);
  const queryClient = useQueryClient();
  const grantedKey = ['ho', 'role-permissions', roleId];
  const catalogue = useQuery({
    queryKey: ['ho', 'permissions'],
    queryFn: () => listAll<Permission>('/api/v1/identity/permissions'),
  });
  const granted = useQuery({
    queryKey: grantedKey,
    queryFn: () => listAll<Granted>(`/api/v1/identity/roles/${roleId}/permissions`),
  });
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());

  const rows = crudApi<Granted, { permissionCode: string }>(hoApi, `/api/v1/identity/roles/${roleId}/permissions`);
  const byCode = new Map((granted.data ?? []).map((g) => [g.permissionCode, g]));
  const catalogueByCode = new Map((catalogue.data ?? []).map((p) => [p.code, p]));
  const held = [...byCode.keys()];
  /** Given by another permission the role holds, not by its own row. */
  const implied = (code: string) => !byCode.has(code) && allows(held, code);

  /** What the role editor gives with a code (ADR 0119): what it needs, unless the role already has it. */
  const needed = (codes: string[]) => {
    const all = new Set(codes);
    for (const code of codes)
      for (const need of catalogueByCode.get(code)?.requires ?? []) if (!allows(held, need)) all.add(need);
    return [...all];
  };

  const change = useMutation({
    mutationFn: async ({ codes, on }: { codes: string[]; on: boolean }) => {
      setBusy((now) => new Set([...now, ...codes]));
      try {
        // One at a time per switch is fine; an area is a few dozen, so they go together.
        const results = await Promise.allSettled(
          codes.map((code) => {
            const row = byCode.get(code);
            return on ? rows.create({ permissionCode: code }) : row ? rows.remove(row) : Promise.resolve();
          }),
        );
        const failed = results.find((r) => r.status === 'rejected');
        if (failed) throw (failed as PromiseRejectedResult).reason;
      } finally {
        setBusy((now) => new Set([...now].filter((c) => !codes.includes(c))));
        await queryClient.invalidateQueries({ queryKey: grantedKey });
      }
    },
  });

  if (catalogue.isLoading || granted.isLoading)
    return <SkeletonText paragraph lineCount={4} aria-label={t('common.loading')} />;
  const failure = catalogue.error ?? granted.error;
  if (failure || !catalogue.data)
    return (
      <InlineNotification
        kind="error"
        role="alert"
        lowContrast
        hideCloseButton
        title={problemMessage(t, asProblem(failure))}
      />
    );

  const describe = (p: Permission) => localized(p.descriptionI18n as Record<string, string> | null, p.description);
  const needle = search.trim().toLowerCase();
  // A retired code is never given again: HO moved its grants to the code that replaced it (ADR 0119).
  const live = catalogue.data.filter((p) => !p.replacedBy);
  const shown = live.filter(
    (p) =>
      !needle ||
      p.code.toLowerCase().includes(needle) ||
      p.description.toLowerCase().includes(needle) ||
      describe(p).toLowerCase().includes(needle),
  );
  const grouped = new Map<string, Permission[]>();
  for (const p of shown) grouped.set(p.module, [...(grouped.get(p.module) ?? []), p]);
  const areas = [...grouped].sort(([a], [b]) => a.localeCompare(b));

  const headers = [
    { key: 'feature', header: t('rolePermissions.feature') },
    { key: 'code', header: t('rolePermissions.code') },
    { key: 'read', header: t('rolePermissions.read') },
    { key: 'manage', header: t('rolePermissions.manage') },
  ];
  // The same widths in every area's table, whatever its text (table-layout: fixed in permissions.scss).
  const columnClass: Record<string, string | undefined> = {
    code: 'pos-permissions__code-column',
    read: 'pos-permissions__check',
    manage: 'pos-permissions__check',
  };
  const main = (row: FeatureRow) => row.manage ?? row.read ?? row.single;
  const label = (row: FeatureRow) => {
    const p = main(row);
    return p ? describe(p) : row.key;
  };
  const sensitive = (row: FeatureRow) => [row.read, row.manage, row.single].some((p) => p?.sensitive);
  const codesOf = (row: FeatureRow) => [row.read, row.manage, row.single].flatMap((p) => (p ? [p.code] : []));
  /** What "all" grants for a line: manage where there is one (it includes read), else read, else the action. */
  const fullOf = (row: FeatureRow) => (row.manage ?? row.read ?? row.single)?.code ?? row.key;
  const box = (permission: Permission, rowLabel: string, column: string) => (
    <Checkbox
      id={`permission-${permission.code}`}
      labelText={
        implied(permission.code)
          ? `${rowLabel}: ${column} (${t('rolePermissions.included')})`
          : `${rowLabel}: ${column}`
      }
      hideLabel
      checked={byCode.has(permission.code) || implied(permission.code)}
      disabled={!canEdit || busy.has(permission.code) || implied(permission.code)}
      onChange={(_event, { checked }) =>
        change.mutate({ codes: checked ? needed([permission.code]) : [permission.code], on: checked })
      }
    />
  );

  return (
    <div className="pos-permissions">
      <div className="pos-permissions__toolbar">
        <Search
          labelText={t('rolePermissions.search')}
          placeholder={t('rolePermissions.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="pos-permissions__tally">
          {t('rolePermissions.count', { granted: byCode.size, total: live.length })}
        </span>
      </div>
      {change.error && (
        <InlineNotification
          kind="error"
          role="alert"
          lowContrast
          hideCloseButton
          title={problemMessage(t, asProblem(change.error))}
        />
      )}
      {areas.length === 0 && <EmptyState heading={t('rolePermissions.none')} />}
      {areas.map(([area, permissions]) => {
        const features = featureRows(permissions);
        // "All": the whole-area lines (they cover their screens, and screens added later), then every line they don't
        // cover, such as an action of the area under another code (`ho.audit` beside `ho.org`).
        const wholeCodes = features.filter((f) => isWholeArea(f, features)).map(fullOf);
        const allCodes = needed(
          [
            ...wholeCodes,
            ...features.map(fullOf).filter((c) => !wholeCodes.includes(c) && !allows([...held, ...wholeCodes], c)),
          ].filter((c) => !byCode.has(c)),
        );
        const full = features.filter((f) => allows(held, fullOf(f)));
        const all = full.length === features.length;
        const areaLabel = t(`rolePermissions.area.${area}` as I18nKey, { defaultValue: area });
        return (
          <section key={area} className="pos-permissions__area" aria-label={areaLabel}>
            <DataTable
              size="md"
              headers={headers}
              rows={features.map((row) => ({ id: row.key, feature: '', code: '', read: '', manage: '' }))}
            >
              {({ rows: tableRows, headers: tableHeaders, getHeaderProps, getRowProps, getTableProps }) => (
                <TableContainer>
                  {/* The area's title and count on the left, its switch on the right, in one row. */}
                  <div className="pos-permissions__header">
                    <div>
                      <h2 className="pos-permissions__title">{areaLabel}</h2>
                      <p className="pos-permissions__tally">
                        {full.length}/{features.length}
                      </p>
                    </div>
                    <Toggle
                      id={`area-${area}`}
                      size="sm"
                      labelText={t('rolePermissions.allPermissions')}
                      labelA={t('rolePermissions.off')}
                      labelB={t('rolePermissions.on')}
                      hideLabel
                      toggled={all}
                      readOnly={!canEdit || change.isPending}
                      onToggle={(next) =>
                        change.mutate(
                          next
                            ? { codes: allCodes, on: true }
                            : { codes: features.flatMap(codesOf).filter((c) => byCode.has(c)), on: false },
                        )
                      }
                    />
                  </div>
                  <Table {...getTableProps()} className="pos-permissions__table" aria-label={areaLabel}>
                    <TableHead>
                      <TableRow>
                        {tableHeaders.map((header) => {
                          const { key, ...props } = getHeaderProps({ header });
                          return (
                            <TableHeader key={key} {...props} className={columnClass[header.key]}>
                              {header.header}
                            </TableHeader>
                          );
                        })}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tableRows.map((tableRow) => {
                        const row = features.find((f) => f.key === tableRow.id);
                        if (!row) return null;
                        // "All of {area}" only when the line covers every line of the group; otherwise say which code it covers.
                        const name = !isWholeArea(row, features)
                          ? label(row)
                          : features.every((f) => f === row || f.key.startsWith(`${row.key}.`))
                            ? t('rolePermissions.wholeArea', { area: areaLabel })
                            : t('rolePermissions.wholeKey', { code: row.key });
                        const needs = [
                          ...new Set([row.read, row.manage, row.single].flatMap((p) => p?.requires ?? [])),
                        ];
                        const kind = main(row)?.dataKind;
                        const { key, ...props } = getRowProps({ row: tableRow });
                        return (
                          <TableRow key={key} {...props}>
                            <TableCell>
                              <span className="pos-permissions__feature">
                                {name}
                                {kind && (
                                  <Tag size="sm" type="cool-gray">
                                    {t(`rolePermissions.dataKind.${kind}` as I18nKey, { defaultValue: kind })}
                                  </Tag>
                                )}
                                {sensitive(row) && (
                                  <Tag size="sm" type="red">
                                    {t('rolePermissions.sensitive')}
                                  </Tag>
                                )}
                              </span>
                              {needs.length > 0 && (
                                <span className="pos-permissions__needs">
                                  {t('rolePermissions.needs', { codes: needs.join(', ') })}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="pos-permissions__code">{row.key}</TableCell>
                            <TableCell className="pos-permissions__check">
                              {row.read && box(row.read, name, t('rolePermissions.read'))}
                            </TableCell>
                            <TableCell className="pos-permissions__check">
                              {row.manage && box(row.manage, name, t('rolePermissions.manage'))}
                              {row.single && box(row.single, name, t('rolePermissions.allowed'))}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DataTable>
          </section>
        );
      })}
    </div>
  );
}
